import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { Skeleton } from '~/components/ui/skeleton';
import { DeliveryStatusChip } from '~/features/deliveries/components/chips/delivery-status-chip';
import { DryRunBadge } from '~/features/deliveries/components/chips/dry-run-badge';
import { productOutcomeTone } from '~/features/deliveries/constants/status-tones';
import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';
import { humanizeEnum } from '~/features/deliveries/utils/format-enum';
import { cn } from '~/utils/cn';

import type { Product } from '../../types/catalog-operations.types';
import { RecuratedSinceChip } from '../chips/recurated-since-chip';

interface Props {
  products: Product[];
  selectedId: string | null;
  onSelect: (product: Product) => void;
  isLoading: boolean;
  /** Delivery audit row per product, when the lean rail-wide lookup has
   *  resolved — absent while it's still loading or hasn't been requested. */
  outcomesByCurationId: Map<string, ProductLookupRow>;
  isLoadingOutcomes: boolean;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * Master list for the compare view — one row per curated product in this
 * batch, badged with its delivery outcome once the lean (no-payload) lookup
 * resolves. Selecting a row drives the attribute table on the right.
 * Pagination lives in the parent (server-paginated, unlike the feed-audit
 * product rail's in-memory search).
 */
export function DeliveryCompareProductRail({
  products,
  selectedId,
  onSelect,
  isLoading,
  outcomesByCurationId,
  isLoadingOutcomes,
  testId = 'delivery-compare-rail',
}: Props) {
  const { t } = useTranslation('catalog-operations');

  if (isLoading) {
    return (
      <div className="space-y-2 p-2" data-testid={`${testId}-loading`}>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div
        className="text-muted-foreground flex h-32 items-center justify-center px-4 text-center text-sm"
        data-testid={`${testId}-empty`}
      >
        {t('deliveryCompare.rail.empty')}
      </div>
    );
  }

  return (
    <ul className="space-y-1.5 p-2" data-testid={`${testId}-list`}>
      {products.map((product) => {
        const isActive = product.id === selectedId;
        const audit = outcomesByCurationId.get(product.id);
        return (
          <li key={product.id}>
            <button
              type="button"
              aria-current={isActive ? 'true' : undefined}
              data-testid={`${testId}-item-${product.id}`}
              onClick={() => {
                onSelect(product);
              }}
              className={cn(
                'w-full cursor-pointer rounded-lg border px-3 py-2.5 text-left transition-all',
                isActive
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/40 hover:bg-muted/40 bg-white',
              )}
            >
              <div className="flex items-start gap-3">
                <CachedImage
                  src={product.imageUrl}
                  alt={product.title}
                  className="h-11 w-11 shrink-0 rounded-md border"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p
                    className={cn(
                      'truncate text-sm font-semibold',
                      isActive ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {product.title}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {product.sourceProductId}
                  </p>
                  <div
                    className="flex flex-wrap items-center gap-1.5"
                    data-testid={`${testId}-item-${product.id}-status`}
                  >
                    <RecuratedSinceChip product={product} className="text-[10px]" />
                    {isLoadingOutcomes ? (
                      <Skeleton
                        className="h-4 w-16 rounded-full"
                        data-testid={`${testId}-item-${product.id}-status-loading`}
                      />
                    ) : audit ? (
                      <>
                        <DeliveryStatusChip
                          tone={productOutcomeTone(audit.outcome ?? 'UNKNOWN')}
                          label={humanizeEnum(audit.outcome ?? 'UNKNOWN')}
                          className="text-[10px]"
                          testId={`delivery-compare-rail-item-${product.id}-outcome-chip`}
                        />
                        {audit.runMode === 'MOCK' ? (
                          <DryRunBadge
                            className="text-[9px]"
                            testId={`delivery-compare-rail-item-${product.id}-dry-run-badge`}
                          />
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted-foreground text-[10px]">
                        {t('deliveryCompare.rail.noRecord')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
